import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Building2, Eye, EyeOff, GitBranch, Landmark, ShieldCheck } from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import DeexPending from "../../components/DeexPending";
import { API } from "../../constant/api";
import { useUser } from "../../context/user-context";
import { useBusinesses } from "../../hooks/use-businesses";
import useAuthAxios from "../../utils/baseAxios";
import { errorMessage } from "../../utils/errorMessage";
import { getDateRange } from "../../utils/GetDateRange";
import { formatAmount, formatCount } from "../../utils/wordFunctions";
import AddBanks from "../banks/AddBanks";
import BankDetails from "../banks/BankDetails";
import ActionCard from "./components/ActionCard";
import StatCard from "./components/StatCard";

/** Radix Select cannot hold an empty-string value, so "all" maps to "". */
const ALL_TIME = "all";

const FILTERS = [
  { value: ALL_TIME, label: "All Time" },
  { value: "today", label: "Today" },
  { value: "thisWeek", label: "This week" },
  { value: "last3Months", label: "Last 3 months" },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const [viewBalance, setViewBalance] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>(ALL_TIME);
  const [openAddBank, setOpenAddBank] = useState<boolean>(false);
  const [openBankDetails, setOpenBankDetails] = useState<boolean>(false);

  const { merchantDetails } = useUser();
  const axiosInstance = useAuthAxios();
  const { current } = getDateRange(filter === ALL_TIME ? "" : filter);

  const {
    businesses,
    business: activeBusiness,
    businessId,
    selectBusiness,
    error: error1,
    isLoading,
  } = useBusinesses();

  const isBranch = !!activeBusiness?.parentBusinessId;
  const parentBusiness = useMemo(
    () =>
      isBranch
        ? businesses?.find(
            (b) => b._id === activeBusiness?.parentBusinessId
          )
        : undefined,
    [isBranch, businesses, activeBusiness]
  );

  useEffect(() => {
    if (error1) {
      const newError = error1 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error1]);

  const {
    data: business,
    error: error2,
    isPending: isBusinessPending,
  } = useQuery({
    queryKey: ["get-business", businessId, current.from, current.to],
    queryFn: async () => {
      const response = await axiosInstance.get(
        API.getBusiness(businessId!, current.from ?? "", current.to ?? "")
      );
      return response.data.data;
    },
    retry: 1,
    enabled: !!businessId,
  });

  useEffect(() => {
    if (error2) {
      const newError = error2 as any;
      toast.error(errorMessage(newError?.message || newError?.data?.message));
    }
  }, [error2]);

  const summary = business?.summary;
  const banks = business?.business?.banks;
  const hasBanks = banks?.length > 0;
  const loading = isLoading || isBusinessPending;
  const masked = !viewBalance;

  const stats = useMemo(
    () => [
      {
        label: "Total payouts processed",
        value: `$${formatAmount(summary?.totalPayouts)}`,
        footer: [
          `${formatAmount(summary?.totalPayoutsInNaira)} NGN`,
          `${formatCount(summary?.payoutsCount)}`,
        ],
      },
      {
        label: "Pending",
        value: `$${formatAmount(summary?.pendingPayouts)}`,
        footer: [
          `${formatAmount(summary?.pendingPayoutsInNaira)} NGN`,
          `${formatCount(summary?.pendingPayoutsCount)}`,
        ],
      },
      {
        label: "Failed",
        value: `$${formatAmount(summary?.failedPayouts)}`,
        footer: [
          `${formatAmount(summary?.failedPayoutsInNaira)} NGN`,
          `${formatCount(summary?.failedPayoutsCounts)}`,
        ],
      },
      { label: "Customers", value: formatCount(summary?.customers) },
      { label: "Products", value: formatCount(summary?.products) },
      { label: "Total sales", value: formatCount(summary?.sales) },
    ],
    [summary]
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] font-manrope">
      <h2 className="font-sora text-[24px] font-bold text-foreground">
        Welcome {merchantDetails?.user?.profile?.firstName},
      </h2>

      {isBranch && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <GitBranch className="size-5 shrink-0 text-primary" />
          <p className="min-w-0 flex-1 text-[13px] font-medium text-foreground">
            You're viewing{" "}
            <span className="font-bold">{activeBusiness?.name}</span>
            {parentBusiness ? (
              <>
                , a branch of{" "}
                <span className="font-bold">{parentBusiness.name}</span>.
              </>
            ) : (
              "."
            )}
          </p>
          {parentBusiness && (
            <button
              type="button"
              onClick={() => selectBusiness(parentBusiness._id)}
              className="shrink-0 text-[12px] font-semibold text-primary underline"
            >
              Switch to parent
            </button>
          )}
        </div>
      )}

      <section className="mt-10">
        <div className="flex flex-col items-start justify-between gap-4 mds:flex-row mds:items-center">
          <h3 className="text-[18px] font-bold text-foreground">Overview</h3>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => setViewBalance((prev) => !prev)}
              className="h-9 gap-2 bg-background text-[11px] font-semibold"
            >
              {masked ? (
                <EyeOff className="size-[18px]" />
              ) : (
                <Eye className="size-[18px]" />
              )}
              {masked ? "Show Balance" : "Hide Balance"}
            </Button>

            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-9 w-[150px] bg-background text-[11px] font-semibold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="font-manrope">
                {FILTERS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="text-[12px]"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 mds:grid-cols-2 lgss:grid-cols-3">
          {stats.map((stat) => (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              footer={stat.footer}
              masked={masked}
              loading={loading}
            />
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h3 className="text-[18px] font-bold text-foreground">
          Finish setting up
        </h3>
        <div className="mt-6 grid grid-cols-1 gap-4 mds:grid-cols-2 lgss:grid-cols-3">
          {/* Once a payout bank exists this prompt is done — adding more is
              handled from the bank details view. */}
          {!hasBanks && (
            <ActionCard
              icon={Landmark}
              title="Set up payment bank"
              description="You have not added a bank"
              actionLabel="Add bank"
              onAction={() => setOpenAddBank(true)}
            />
          )}
          <ActionCard
            icon={Building2}
            title="View bank details"
            description={
              hasBanks
                ? "View your bank details"
                : "You have not added a bank"
            }
            actionLabel="View banks"
            onAction={() => setOpenBankDetails(true)}
          />
          <ActionCard
            icon={ShieldCheck}
            title="Security"
            description="Set up your security settings to protect your account"
            actionLabel="Set up security"
            onAction={() => navigate("/settings/security")}
          />
        </div>
      </section>

      {!isLoading && (!businesses || businesses.length < 1) ? <DeexPending /> : null}
      {openAddBank && (
        <AddBanks
          setOpenAddBank={setOpenAddBank}
          businessID={businessId!}
        />
      )}
      {openBankDetails && (
        <BankDetails
          setOpenBankDetails={setOpenBankDetails}
          banks={banks}
          businessID={businessId!}
        />
      )}
    </div>
  );
};

export default Dashboard;
